import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, signal } from '@angular/core';
import { Piece } from '@/app/common/general-types/briefs.dto';
import { editorFromSavedPieces, piecesCopiedFrom, PieceEditorState } from '@/app/common/brief-pieces/piece-list';
import { PLACEMENT_CHANNEL_LABEL } from '@/app/common/brief-pieces/piece-rules';
import { CreateBriefService } from '@/app/pages/briefs/service/create-brief/create-brief.service';
import { ContentSection } from '@/app/pages/create-brief-select/sections/content-section/content-section';
import { MbaIcon } from '../../assignment/atoms/mba-icon/mba-icon';
import {
  briefKind,
  DeliverableVideo,
  deliverableVideos,
  DURATION_LABEL,
  legacyDuration,
  ORIENTATION_LABEL,
  plural,
  pricingForDuration,
  sectionStatus,
  sharedValue,
} from '../briefing.helpers';
import { BriefingService } from '../briefing.service';
import { bf } from '../briefing-ui';
import { BriefingDrawer } from '../molecules/briefing-drawer/briefing-drawer';
import { BriefingSection } from '../molecules/briefing-section/briefing-section';
import { BriefingPostRequirements } from '../molecules/briefing-post-requirements/briefing-post-requirements';

type Snapshot = {
  pieceEditor: PieceEditorState | null;
  pieces: Piece[] | null;
  videosPerCreator: number;
  selectedAdvertisingOnly: boolean;
};

/**
 * Deliverables: the videos each creator makes, their format and length, and where they post.
 * Piece briefs are edited with the brief builder's piece editor until someone is hired (the API
 * refuses after that); legacy briefs can only change the video length, which re-prices them.
 */
@Component({
  selector: 'mbt-briefing-deliverables',
  imports: [BriefingSection, BriefingDrawer, MbaIcon, ContentSection, BriefingPostRequirements],
  template: `
    @if (svc.brief(); as brief) {
    <mbt-briefing-section key="dist" icon="clapperboard" title="Deliverables" [flash]="flash()"
      [helper]="influence() ? 'What each hired creator makes and posts. Every creator delivers the same set.' : 'What each hired creator makes and sends to you. Every creator delivers the same set.'"
      [status]="status()" (edit)="open()">
      @if (videos().length) {
        <span class="text-[13px] font-semibold text-(--fg-2)">Each hired creator delivers:</span>
        <ul class="m-0 grid list-none grid-cols-2 gap-3 p-0 max-[640px]:grid-cols-1">
          @for (video of videos(); track $index) {
            <li class="flex items-center gap-3.5 rounded-lg border border-(--border-subtle) p-3.5">
              <span [class]="thumbClass(video)" aria-hidden="true"><mba-icon name="play" size="size-4 fill-white" /></span>
              <span class="flex min-w-0 flex-col gap-1">
                <span [class]="bf.k">Video {{ $index + 1 }}</span>
                <b [class]="bf.display + ' text-lg'">{{ video.label }}</b>
                <span [class]="bf.sub">{{ orientation[video.orientation].name }} {{ orientation[video.orientation].ratio }} · {{ durations[video.duration] }}</span>
                @if (video.channels.length) {
                  <span class="flex flex-wrap gap-1">
                    @for (channel of video.channels; track channel) { <span [class]="bf.tag">{{ channel }}</span> }
                  </span>
                } @else if (influence()) {
                  <span [class]="bf.sub">Sent to you, not posted</span>
                }
              </span>
            </li>
          }
        </ul>
        <div class="flex flex-wrap gap-2">
          @if (sharedOrientation(); as o) {
            <span class="inline-flex items-center gap-1.5 rounded-full bg-(--bg-alt) px-3 py-1.5 text-[13px] font-semibold">
              <mba-icon name="smartphone" size="size-4" />{{ orientation[o].name }} {{ orientation[o].ratio }}<span [class]="bf.sub">{{ orientation[o].use }}</span></span>
          }
          @if (sharedDuration(); as d) {
            <span class="inline-flex items-center gap-1.5 rounded-full bg-(--bg-alt) px-3 py-1.5 text-[13px] font-semibold"><mba-icon name="timer" size="size-4" />{{ durations[d] }} per video</span>
          }
          <span class="inline-flex items-center gap-1.5 rounded-full bg-(--bg-alt) px-3 py-1.5 text-[13px] font-semibold"><mba-icon name="film" size="size-4" />{{ videoText() }} per creator</span>
        </div>
      } @else {
        <div [class]="bf.empty">
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <b [class]="bf.emptyTitle">No videos set</b>
            <span [class]="bf.sub">Add the videos each creator makes.</span>
          </span>
          @if (canEditPieces()) {
            <button type="button" [class]="bf.btnPrimary" (click)="open()"><mba-icon name="plus" size="size-4" />Set up</button>
          }
        </div>
      }
      @if (!influence()) {
        <div [class]="bf.info"><mba-icon name="info" size="size-4" /><span>UGC Only: videos come straight to you. Nothing is posted on the creator’s socials.</span></div>
      }
      @if (svc.hired()) {
        <div [class]="bf.infoLock"><mba-icon name="lock" size="size-4" /><span>The videos are locked because {{ hiredText() }} hired.</span></div>
      }
      @if (captions().length) {
        <div class="flex flex-col gap-2">
          <b [class]="bf.display + ' text-[19px]'">Post requirements</b>
          @for (item of captions(); track $index) {
            <div [class]="bf.box + ' flex flex-col gap-1 px-3.5 py-2.5 text-[13px]'">
              <span [class]="bf.k">{{ item.label }} · {{ item.channel }}</span>
              @if (item.caption) { <span><b>Caption:</b> {{ item.caption }}</span> }
              @if (item.hashtags) { <span><b>Hashtags:</b> {{ item.hashtags }}</span> }
              @if (item.link) { <span><b>Link:</b> {{ item.link }}</span> }
            </div>
          }
        </div>
      }
      @if (!brief.pieces?.length && brief.shoutBrief) {
        <mbt-briefing-post-requirements class="block" />
      }
    </mbt-briefing-section>

    @if (editing()) {
    <mbt-briefing-drawer icon="clapperboard" title="Deliverables" [mode]="status() === 'missing' ? 'Set up' : 'Edit'"
      helper="What each hired creator makes. Every creator delivers the same set."
      [saving]="saving()" [canSave]="!svc.hired()" [note]="svc.hired() ? 'Locked while creators are hired' : ''"
      (closed)="close()" (save)="save()">
      @if (svc.hired()) {
        <div [class]="bf.infoLock"><mba-icon name="lock" size="size-4" /><span>The videos are locked because {{ hiredText() }} hired.</span></div>
      } @else if (brief.pieces?.length) {
        <bb-content-section [showPrice]="false" />
      } @else {
        <div [class]="bf.field">
          <span [class]="bf.fieldLabel">Length of each video</span>
          <div class="flex flex-wrap gap-2" role="radiogroup" aria-label="Length of each video">
            @for (seconds of lengths; track seconds) {
              <button type="button" role="radio" [attr.aria-checked]="length() === seconds"
                [class]="length() === seconds ? bf.chipOn : bf.chipOff" (click)="length.set(seconds)">{{ durations[seconds] }}</button>
            }
          </div>
          <span [class]="bf.fieldSub">Changing the length re-prices the brief for creators you hire next.</span>
        </div>
        <div [class]="bf.info"><mba-icon name="info" size="size-4" /><span>{{ videoText() }} per creator, {{ orientation[videos()[0]?.orientation ?? 'vertical'].name.toLowerCase() }}. The number and format of videos on this brief can’t be changed here.</span></div>
      }
    </mbt-briefing-drawer>
    }
    }
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingDeliverables implements OnDestroy {
  protected readonly bf = bf;
  protected readonly orientation = ORIENTATION_LABEL;
  protected readonly durations = DURATION_LABEL;
  protected readonly lengths = [15, 30, 60] as const;
  protected readonly svc = inject(BriefingService);
  private readonly createBrief = inject(CreateBriefService);

  readonly flash = input(false);

  protected readonly status = computed(() => sectionStatus('dist', this.svc.brief()!));
  protected readonly influence = computed(() => briefKind(this.svc.brief()!) === 'influence');
  protected readonly videos = computed(() => deliverableVideos(this.svc.brief()!));
  protected readonly sharedOrientation = computed(() => sharedValue(this.videos().map((v) => v.orientation)));
  protected readonly sharedDuration = computed(() => sharedValue(this.videos().map((v) => v.duration)));
  protected readonly videoText = computed(() => plural(this.videos().length, 'video'));
  protected readonly hiredText = computed(() => {
    const n = this.svc.hired();
    return `${plural(n, 'creator')} ${n === 1 ? 'is' : 'are'}`;
  });
  protected readonly canEditPieces = computed(() => !this.svc.hired());

  /** Per-placement post requirements on a piece brief (legacy briefs use the molecule below). */
  protected readonly captions = computed(() =>
    (this.svc.brief()?.pieces ?? []).flatMap((piece) =>
      piece.placements.flatMap((p) =>
        p.kind === 'social' && (p.caption || p.hashtags || p.callToActionLink)
          ? [{ label: piece.label, channel: PLACEMENT_CHANNEL_LABEL[p.channel], caption: p.caption, hashtags: p.hashtags, link: p.callToActionLink }]
          : [],
      ),
    ),
  );

  protected readonly editing = signal(false);
  protected readonly saving = signal(false);
  protected readonly length = signal<15 | 30 | 60>(30);
  private snapshot: Snapshot | null = null;

  protected thumbClass(video: DeliverableVideo): string {
    const size = video.orientation === 'square' ? 'h-10 w-10' : video.orientation === 'horizontal' ? 'h-10 w-[62px]' : 'h-[62px] w-10';
    return `${size} grid shrink-0 place-items-center rounded-lg bg-(image:--app-grad) text-white`;
  }

  open(): void {
    const brief = this.svc.brief();
    if (!brief) {
      return;
    }
    if (brief.pieces?.length && !this.svc.hired()) {
      this.snapshot = {
        pieceEditor: this.createBrief.pieceEditor(),
        pieces: this.createBrief.pieces(),
        videosPerCreator: this.createBrief.videosPerCreator(),
        selectedAdvertisingOnly: this.createBrief.selectedAdvertisingOnly(),
      };
      const pieces = piecesCopiedFrom(brief.pieces);
      this.createBrief.pieceEditor.set(pieces?.length ? editorFromSavedPieces(pieces) : null);
      this.createBrief.pieces.set(pieces);
    }
    this.length.set(legacyDuration(brief.currentPrice));
    this.editing.set(true);
  }

  protected close(): void {
    this.restore();
    this.editing.set(false);
  }

  private restore(): void {
    const snapshot = this.snapshot;
    if (!snapshot) {
      return;
    }
    this.createBrief.pieceEditor.set(snapshot.pieceEditor);
    this.createBrief.pieces.set(snapshot.pieces);
    this.createBrief.videosPerCreator.set(snapshot.videosPerCreator);
    this.createBrief.selectedAdvertisingOnly.set(snapshot.selectedAdvertisingOnly);
    this.snapshot = null;
  }

  ngOnDestroy(): void {
    this.restore();
  }

  protected async save(): Promise<void> {
    const brief = this.svc.brief();
    if (!brief) {
      return;
    }
    if (brief.pieces?.length) {
      const pieces = this.createBrief.pieces();
      if (!pieces?.length || pieces.some((piece) => !piece.label.trim())) {
        this.svc.error('Name every video, and give each one a placement or UGC only');
        return;
      }
      await this.run([() => this.svc.pieces(pieces)]);
      return;
    }
    if (this.length() === legacyDuration(brief.currentPrice)) {
      this.close();
      return;
    }
    const pricing = pricingForDuration(brief, this.length());
    if (!pricing) {
      this.svc.error('This brief has no price to change.');
      return;
    }
    await this.run([() => this.svc.pricing(pricing)]);
  }

  private async run(steps: Parameters<BriefingService['save']>[0]): Promise<void> {
    this.saving.set(true);
    const ok = await this.svc.save(steps, 'Deliverables saved');
    this.saving.set(false);
    if (ok) {
      this.close();
    }
  }
}
